/**
 * pages/email/ConnectDevices.tsx — "Connect your devices"
 *
 * Route: /dashboard/email/connect/v2/:id   (id = mx1_mail_domains row)
 *
 * Left column: mailbox picker + the live connection settings (IMAP / SMTP /
 * POP3) with copy buttons, an "Automatic setup" status strip and the
 * sending limits. Right column: per-client guides (tabs) that inline the
 * concrete values. Print-friendly: the print stylesheet hides nav/tabs and
 * prints every guide in sequence.
 *
 * Data: GET /email/setup/v2/domains/:id/connection-settings (falls back to
 * config/mail.ts constants while loading or if it fails),
 * GET …/mailboxes for the picker, GET …/mailboxes/:mailboxId/mobileconfig
 * for the iOS profile (bearer auth → fetched as a blob).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashShell from '../../components/dash/DashShell';
import { ConnectionSettings, ConnectionSettingsPayload, resolveConnectionSettings, securityLabel } from '../../config/mail';
import { api, apiBlob, apiData, friendlyError, saveBlob, useCopy, v2Base } from '../../components/email/emailApi';
import { DeviceGuide, GUIDES, GuideContext, GuideId } from '../../components/email/DeviceGuide';

interface MailboxLite { id: number; email: string; active: boolean }
interface DomainSummary { id: string; domain: string }

const PRINT_CSS = `
@media print {
  .aurora-bg, aside, nav, .connect-no-print, button[aria-label="Open menu"] { display: none !important; }
  .lg\\:pl-60 { padding-left: 0 !important; }
  .dash-root { background: #fff !important; }
  .connect-print-only { display: block !important; }
  .connect-screen-only { display: none !important; }
  details { break-inside: avoid; }
  details > summary { list-style: none; }
  a[href]::after { content: none !important; }
}
`;

const STORAGE_KEY = 'be2.connect.guide';

const SettingRow: React.FC<{
  label: string;
  value: string | number;
  onCopy: (text: string, label: string) => void;
  copiedLabel: string | null;
  mono?: boolean;
}> = ({ label, value, onCopy, copiedLabel, mono = true }) => {
  const text = String(value);
  const isCopied = copiedLabel === label;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <dt className="text-xs text-gray-500 w-24 flex-none">{label}</dt>
      <dd className={`flex-1 min-w-0 text-sm text-primary-navy break-all ${mono ? 'font-mono' : ''}`}>{text}</dd>
      <button
        type="button"
        onClick={() => onCopy(text, label)}
        aria-label={`Copy ${label}: ${text}`}
        className={`connect-no-print flex-none text-xs px-2 py-1 rounded border ${isCopied ? 'bg-secondary-500 border-secondary-500 text-primary-navy font-semibold' : 'bg-white border-gray-300 text-primary-navy hover:bg-secondary-50'}`}
      >
        {isCopied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const StatusTick: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${ok ? 'bg-secondary-500 border-secondary-500 text-primary-navy font-semibold' : 'bg-white border-gray-300 text-gray-600'}`}>
    <span aria-hidden>{ok ? '✓' : '○'}</span>
    {label}: {ok ? 'detected' : 'not detected'}
  </span>
);

const ConnectDevices: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [domain, setDomain] = useState<DomainSummary | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxLite[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [manualEmail, setManualEmail] = useState('');
  const [payload, setPayload] = useState<ConnectionSettingsPayload | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guide, setGuide] = useState<GuideId>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY) as GuideId | null;
      return v && GUIDES.some(g => g.id === v) ? v : 'iphone';
    } catch { return 'iphone'; }
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { copied, copy } = useCopy(2000);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const load = useCallback(async () => {
    if (!id) return;
    const base = v2Base(id);
    const [domR, mbR, csR] = await Promise.all([
      api<{ success: boolean; data: DomainSummary[] }>('/email/setup/v2').catch(() => null),
      api<{ success: boolean; data: MailboxLite[]; error?: string }>(`${base}/mailboxes`).catch((e: any) => { setError(friendlyError(e, 'Couldn\'t load mailboxes')); return null; }),
      apiData<ConnectionSettingsPayload>(`${base}/connection-settings`).catch((e: any) => { setSettingsErr(friendlyError(e, 'Showing default settings')); return null; }),
    ]);
    if (domR?.success && Array.isArray(domR.data)) {
      const d = domR.data.find(x => x.id === id);
      if (d) setDomain(d);
    }
    if (mbR?.success) {
      const list = Array.isArray(mbR.data) ? mbR.data : [];
      setMailboxes(list);
      setSelectedId(prev => (prev && list.some(m => m.id === prev)) ? prev : (list[0]?.id ?? null));
      setError(null);
    }
    if (csR) { setPayload(csR); setSettingsErr(null); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, guide); } catch { /* ignore */ } }, [guide]);

  const settings: ConnectionSettings = useMemo(() => resolveConnectionSettings(payload), [payload]);
  const selected = mailboxes.find(m => m.id === selectedId) || null;
  const domainName = domain?.domain || (mailboxes[0]?.email.split('@')[1]) || 'yourdomain.com';
  const email = selected?.email || manualEmail.trim() || `you@${domainName}`;
  const mailboxesPath = `/dashboard/email/mailboxes/v2/${id}`;
  const setupPath = `/dashboard/email/setup/v2/${id}`;

  const downloadProfile = useCallback(async () => {
    if (!id || !selected) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const { blob, filename } = await apiBlob(`${v2Base(id)}/mailboxes/${selected.id}/mobileconfig`);
      saveBlob(blob, filename || `${selected.email.replace(/[^a-z0-9.@_-]/gi, '_')}.mobileconfig`);
    } catch (e: any) {
      setDownloadError(friendlyError(e, 'Couldn\'t prepare the profile — use the manual steps below.'));
    } finally { setDownloading(false); }
  }, [id, selected]);

  const ctx: GuideContext = {
    settings,
    email,
    domain: domainName,
    mailboxesPath,
    onDownloadProfile: downloadProfile,
    downloading,
    downloadError,
    hasMailbox: !!selected,
  };

  const onTabKey = (e: React.KeyboardEvent, idx: number) => {
    const n = GUIDES.length;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % n;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next === null) return;
    e.preventDefault();
    setGuide(GUIDES[next].id);
    tabRefs.current[next]?.focus();
  };

  const auto = settings.autoconfig.status;
  const allAuto = auto.autoconfig && auto.autodiscover && auto.srv;

  if (loading) {
    return (
      <DashShell title="Connect your devices" subtitle="Loading…">
        <div className="h-28 rounded-2xl bg-white/60 backdrop-blur animate-pulse mb-6" />
        <div className="h-64 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }

  return (
    <DashShell
      title="Connect your devices"
      subtitle={`Set up ${domainName} mail in any app — phone, desktop or Gmail.`}
      right={
        <button type="button" onClick={() => window.print()} className="connect-no-print text-sm bg-white/80 backdrop-blur border border-white/40 text-primary-navy px-3 py-1.5 rounded-lg hover:border-secondary-500 transition">
          Print all guides
        </button>
      }
    >
      <style>{PRINT_CSS}</style>
      <div className="space-y-6">
        <div className="connect-no-print flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link to="/customer/email" className="text-primary-navy hover:underline">← Email dashboard</Link>
          <Link to={mailboxesPath} className="text-primary-navy hover:underline">Mailboxes</Link>
          <Link to={setupPath} className="text-primary-navy hover:underline">DNS &amp; setup</Link>
        </div>

        {error && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">🚨 {error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-6 items-start">
          {/* LEFT — picker + settings */}
          <div className="space-y-4 lg:sticky lg:top-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div>
                <label htmlFor="connect-mailbox" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Mailbox</label>
                {mailboxes.length > 0 ? (
                  <select
                    id="connect-mailbox"
                    value={selectedId ?? ''}
                    onChange={e => setSelectedId(Number(e.target.value) || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono bg-white focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  >
                    {mailboxes.map(m => (
                      <option key={m.id} value={m.id}>{m.email}{m.active ? '' : ' (suspended)'}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      id="connect-mailbox"
                      value={manualEmail}
                      onChange={e => setManualEmail(e.target.value)}
                      placeholder={`you@${domainName}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      No mailboxes yet — <Link to={mailboxesPath} className="underline">create one</Link> to enable the iPhone profile download.
                    </p>
                  </>
                )}
                {selected && !selected.active && (
                  <p role="status" className="text-xs text-primary-navy mt-1">⏸ This mailbox is suspended — it can't sign in until it's reactivated.</p>
                )}
              </div>

              {settingsErr && (
                <p role="status" className="text-xs text-primary-navy bg-secondary-50 border border-secondary-200 rounded-lg p-2">
                  ⚠ {settingsErr}. Values below are our standard defaults.
                </p>
              )}

              <div>
                <h3 className="text-sm font-semibold text-primary-navy mb-1">Incoming — IMAP <span className="font-normal text-gray-500">(recommended)</span></h3>
                <dl className="divide-y divide-gray-100">
                  <SettingRow label="Server" value={settings.imap.host} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Port" value={settings.imap.port} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Security" value={securityLabel(settings.imap.security)} onCopy={copy} copiedLabel={copied} mono={false} />
                  <SettingRow label="Username" value={email} onCopy={copy} copiedLabel={copied} />
                </dl>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-navy mb-1">Outgoing — SMTP</h3>
                <dl className="divide-y divide-gray-100">
                  <SettingRow label="Server" value={settings.smtp.host} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Port" value={settings.smtp.port} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Security" value={securityLabel(settings.smtp.security)} onCopy={copy} copiedLabel={copied} mono={false} />
                  <SettingRow label="Auth" value={settings.smtp.auth} onCopy={copy} copiedLabel={copied} mono={false} />
                  <SettingRow label="Username" value={email} onCopy={copy} copiedLabel={copied} />
                </dl>
              </div>
              <details className="group">
                <summary className="cursor-pointer text-sm font-semibold text-primary-navy select-none">
                  <span aria-hidden className="inline-block transition-transform group-open:rotate-90">▸</span> Incoming — POP3 <span className="font-normal text-gray-500">(downloads &amp; removes mail)</span>
                </summary>
                <dl className="divide-y divide-gray-100 mt-1">
                  <SettingRow label="Server" value={settings.pop3.host} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Port" value={settings.pop3.port} onCopy={copy} copiedLabel={copied} />
                  <SettingRow label="Security" value={securityLabel(settings.pop3.security)} onCopy={copy} copiedLabel={copied} mono={false} />
                </dl>
              </details>
              <div className="text-xs text-primary-navy/80 bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                <strong>Password</strong> = the mailbox password (set in <Link to={mailboxesPath} className="underline">Mailboxes</Link>), not your 12and22 login.
                Webmail: <a href={settings.webmail_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{settings.webmail_url}</a>
              </div>
              <div className="sr-only" role="status" aria-live="polite">{copied ? `${copied} copied to clipboard` : ''}</div>
            </div>

            {/* Automatic setup strip */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h3 className="text-sm font-semibold text-primary-navy">Automatic setup</h3>
                {!allAuto && (
                  <Link to={setupPath} className="connect-no-print text-xs text-primary-navy underline">Add the optional DNS records →</Link>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusTick ok={auto.autoconfig} label="Thunderbird autoconfig" />
                <StatusTick ok={auto.autodiscover} label="Outlook autodiscover" />
                <StatusTick ok={auto.srv} label="SRV records" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {allAuto
                  ? 'Thunderbird, Outlook and most apps will find the servers from just the address and password.'
                  : 'With these records in place, apps find the servers from just the address and password. Until then, use the manual steps.'}
              </p>
            </div>

            {/* Limits */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-primary-navy mb-2">Sending limits</h3>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                  <dt className="text-xs text-gray-500">Outbound per mailbox</dt>
                  <dd className="font-semibold text-primary-navy">{settings.limits.outbound_per_hour} / hour</dd>
                </div>
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                  <dt className="text-xs text-gray-500">Message size</dt>
                  <dd className="font-semibold text-primary-navy">{settings.limits.max_message_mb} MB <span className="text-xs font-normal text-gray-500">(~{settings.limits.attachment_mb} MB attachments)</span></dd>
                </div>
              </dl>
              {settings.send_as_aliases && (
                <p className="text-xs text-gray-500 mt-2">
                  Mailboxes can send from any alias that forwards to them — add the alias as an extra From address in your app.
                </p>
              )}
            </div>
          </div>

          {/* RIGHT — guides */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div role="tablist" aria-label="Device guides" className="connect-no-print flex flex-wrap gap-1 p-3 border-b border-gray-100">
              {GUIDES.map((g, i) => {
                const active = g.id === guide;
                return (
                  <button
                    key={g.id}
                    ref={el => { tabRefs.current[i] = el; }}
                    role="tab"
                    id={`tab-${g.id}`}
                    aria-selected={active}
                    aria-controls={`panel-${g.id}`}
                    tabIndex={active ? 0 : -1}
                    onClick={() => setGuide(g.id)}
                    onKeyDown={e => onTabKey(e, i)}
                    className={`text-sm px-3 py-1.5 rounded-lg border transition ${active ? 'bg-primary-navy text-white border-primary-navy' : 'bg-white text-primary-navy border-gray-200 hover:border-secondary-500'}`}
                  >
                    <span className="hidden sm:inline">{g.label}</span>
                    <span className="sm:hidden">{g.short}</span>
                  </button>
                );
              })}
            </div>

            {/* Screen: active guide only */}
            <div className="connect-screen-only p-5">
              {GUIDES.filter(g => g.id === guide).map(g => (
                <div key={g.id} role="tabpanel" id={`panel-${g.id}`} aria-labelledby={`tab-${g.id}`}>
                  <h2 className="text-lg font-semibold text-primary-navy mb-3">{g.label}</h2>
                  <DeviceGuide guide={g.id} ctx={ctx} />
                </div>
              ))}
            </div>

            {/* Print: every guide */}
            <div className="connect-print-only hidden p-5 space-y-8">
              {GUIDES.map(g => (
                <section key={g.id}>
                  <h2 className="text-lg font-semibold text-primary-navy mb-3">{g.label}</h2>
                  <DeviceGuide guide={g.id} ctx={{ ...ctx, onDownloadProfile: undefined, printAll: true }} />
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashShell>
  );
};

export default ConnectDevices;
